import React, { useState } from 'react';
import { F } from '../styles/theme';
import { useTheme } from '../context/ThemeContext';
import { useUserPlan, PRICING } from '../context/UserPlanContext';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useT } from '../i18n/useT';
import { MCGridMark } from '../components/red/MemberCard';

// Calm-Designsprache (Mockup: brand/pricing-calm.html, vom User abgenommen).
// Konzept: Der €19-Report ist der Held — als DOKUMENT (kein Abo). Free/Premium/VIP
// sind echte red.-Pässe mit der MemberCard-Palette (theme-unabhängig) + geprägtem
// „+"-Wasserzeichen. Rot lebt NUR auf dem Marken-Punkt. Buttons = Ink (kein Rot),
// keine roten Container, kein Sheen, kein Caps-Kicker (siehe Calm-Doktrin).

const RED = '#E4544A';
const YEAR = new Date().getFullYear();

// MemberCard-Paletten — fixe Brand-Artefakt-Werte, bewusst UNABHÄNGIG vom App-Theme
// (genau wie die echte Karte: free Creme · premium warmes Creme · vip dunkel).
const PASS = {
  free: {
    bg: 'linear-gradient(155deg,#FDFCFA,#F2EEE5)',
    ink: '#1B1B1E', sub: '#6E665A', faint: '#A89F90',
    hair: 'rgba(27,27,30,0.12)', cells: '#1B1B1E',
    emboss: { cells: 'rgba(27,27,30,0.028)', dot: 'rgba(27,27,30,0.05)', plus: 'rgba(27,27,30,0.09)' },
    btnBg: '#1B1B1E', btnFg: '#FBF7EE',
    shadow: '0 1px 2px rgba(60,48,28,0.05), 0 14px 38px rgba(60,48,28,0.10)',
  },
  premium: {
    bg: 'linear-gradient(155deg,#F6F0E6,#EAE1D0)',
    ink: '#1B1B1E', sub: '#6E665A', faint: '#8A8378',
    hair: 'rgba(27,27,30,0.12)', cells: '#1B1B1E',
    emboss: { cells: 'rgba(27,27,30,0.04)', dot: 'rgba(27,27,30,0.06)', plus: 'rgba(27,27,30,0.12)' },
    btnBg: '#1B1B1E', btnFg: '#FBF7EE',
    shadow: '0 1px 2px rgba(60,48,28,0.06), 0 16px 40px rgba(60,48,28,0.12)',
  },
  vip: {
    bg: 'linear-gradient(155deg,#1B1B1F,#0E0E11)',
    ink: '#FDFCFA', sub: '#B9B1A4', faint: '#7E776B',
    hair: 'rgba(255,255,255,0.12)', cells: '#EDE7DB',
    emboss: { cells: 'rgba(255,255,255,0.028)', dot: 'rgba(255,255,255,0.055)', plus: 'rgba(255,255,255,0.11)' },
    btnBg: '#EDE7DB', btnFg: '#1B1B1E',
    shadow: '0 14px 40px rgba(15,10,8,0.42)',
  },
};

const buildPlans = (t) => [
  {
    tier: 'Free',
    price: '€0',
    note: t('price_note_forever'),
    feats: [
      t('price_feat_free_unlimited'),
      t('price_feat_free_track'),
      t('price_feat_free_live'),
      t('price_feat_free_import'),
      t('price_feat_free_reports_addon', { price: PRICING.report.price }),
    ],
    cta: t('price_cta_free_start'),
  },
  {
    tier: 'Report',
    price: `€${PRICING.report.price}`,
    note: t('price_note_per_year_once'),
    feats: [
      t('price_feat_report_excel_pdf'),
      t('price_feat_report_all_tx'),
      t('price_feat_report_charts'),
      t('price_feat_report_no_sub'),
    ],
    cta: t('price_cta_report_buy'),
  },
  {
    tier: 'Premium',
    price: `€${PRICING.premium.price}`,
    note: t('price_note_per_year'),
    feats: [
      t('price_feat_premium_5000tx'),
      t('price_feat_premium_ai_upload'),
      t('price_feat_premium_free_reports'),
      t('price_feat_premium_per_coin'),
      t('price_feat_premium_smart_check'),
      t('price_feat_premium_exchange_apis'),
    ],
    cta: t('price_cta_premium_unlock'),
  },
  {
    tier: 'VIP',
    price: `€${PRICING.vip.price}`,
    note: t('price_note_per_year'),
    feats: [
      t('price_feat_vip_all_premium'),
      t('price_feat_vip_unlimited'),
      t('price_feat_vip_priority'),
      t('price_feat_vip_early_access'),
      t('price_feat_vip_code'),
    ],
    cta: t('price_cta_vip_become'),
  },
];

const tierKeyOf = (tier) => tier.toLowerCase(); // 'free'|'report'|'premium'|'vip'

// red. Wortmarke (Plus Jakarta, Punkt = Marken-Koralle). color/dot überschreibbar
// damit sie auf den dunklen VIP-Pass passt.
function Wordmark({ size = 20, color, dot = RED }) {
  const d = size * 0.18;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'baseline',
      fontFamily: F.brand, fontWeight: 800, letterSpacing: '-0.04em',
      lineHeight: 1, fontSize: size, color,
    }}>
      red<span aria-hidden style={{ display: 'inline-block', width: d, height: d, marginLeft: d * 0.5, background: dot, borderRadius: d * 0.2 }} />
    </span>
  );
}

// Häkchen (dünn, neutral) — ersetzt die alten roten 7px-Quadrate.
function Check({ size = 15, stroke, sw = 2.1 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
      <path d="M5 12.5l4.5 4.5L19 7" />
    </svg>
  );
}

export default function PricingScreen({ onClose }) {
  const { T } = useTheme();
  const { t } = useT();
  const { tier: currentTier, setPlan, markReportPurchased, hasReportForYear } = useUserPlan();
  const { isMobile, isTablet, isDesktop } = useBreakpoint();
  const [busy, setBusy] = useState(null);
  const reportOwned = hasReportForYear(YEAR);
  const PLANS = buildPlans(t);
  const report = PLANS.find(p => p.tier === 'Report');
  const passes = PLANS.filter(p => p.tier !== 'Report'); // Free · Premium · VIP

  const activate = async (key) => {
    setBusy(key);
    try {
      if (key === 'report') {
        await markReportPurchased(YEAR);
      } else {
        await setPlan(key, { source: 'mock_activation' });
      }
      onClose?.();
    } finally {
      setBusy(null);
    }
  };

  // CTA-Logik (Plan-Status-aware) — unverändert übernommen.
  const planMeta = (p) => {
    const key = tierKeyOf(p.tier);
    let ctaLabel = p.cta;
    let ctaDisabled = false;
    if (key === 'free') {
      ctaDisabled = currentTier === 'free';
      if (ctaDisabled) ctaLabel = t('price_cta_current_plan');
    } else if (key === 'report') {
      ctaDisabled = reportOwned || currentTier === 'premium' || currentTier === 'vip';
      if (reportOwned) ctaLabel = t('price_cta_already_bought', { year: YEAR });
      else if (currentTier === 'premium' || currentTier === 'vip') ctaLabel = t('price_cta_in_your_plan');
    } else if (key === 'premium') {
      ctaDisabled = currentTier === 'premium' || currentTier === 'vip';
      if (currentTier === 'premium') ctaLabel = t('price_cta_current_plan');
      else if (currentTier === 'vip') ctaLabel = t('price_cta_in_vip');
    } else {
      ctaDisabled = currentTier === 'vip';
      if (ctaDisabled) ctaLabel = t('price_cta_current_plan');
    }
    return { key, ctaLabel, ctaDisabled };
  };

  const passCols = isMobile ? 1 : isTablet ? 2 : 3;

  const inner = (
    <div style={{ display: 'flex', flexDirection: 'column', fontFamily: F.family, color: T.text }}>

      {/* Header — calm: sentence-case Headline, KEIN Caps-Kicker, KEIN 2px-Strich.
          Im Overlay-Modus trägt der Sticky-Header schon den Titel. */}
      {!onClose && (
        <div style={{ marginBottom: isMobile ? 26 : 34 }}>
          <h2 style={{
            fontFamily: F.display, fontSize: isMobile ? 34 : 46, fontWeight: 800,
            letterSpacing: '-0.04em', lineHeight: 1.02, margin: 0, color: T.text,
          }}>{t('price_title')}</h2>
          <p style={{ margin: '14px 0 0', fontSize: 16, color: T.sub, lineHeight: 1.5, maxWidth: 600 }}>
            {t('price_subtitle')}
          </p>
        </div>
      )}

      {/* ── Report-Hero (Dokument, kein Pass) ── */}
      <ReportHero
        T={T} t={t} report={report} isMobile={isMobile}
        meta={planMeta(report)} busy={busy === 'report'}
        onCta={() => activate('report')}
      />

      {/* ── Mitgliedschaften = Pässe ── */}
      <div style={{
        display: 'grid', gap: 18, marginTop: 18,
        gridTemplateColumns: `repeat(${passCols}, minmax(0, 1fr))`,
      }}>
        {passes.map(p => (
          <PassCard
            key={p.tier}
            pal={PASS[tierKeyOf(p.tier)]}
            plan={p}
            meta={planMeta(p)}
            busy={busy === tierKeyOf(p.tier)}
            activating={t('price_activating')}
            onCta={() => activate(tierKeyOf(p.tier))}
          />
        ))}
      </div>

      {/* ── Trust-Zeile (leise, Hairline) ── */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '10px 22px',
        marginTop: 34, paddingTop: 22, borderTop: `1px solid ${T.line}`,
      }}>
        {[t('price_trust_gdpr'), t('price_trust_eu'), t('price_trust_no_advice'), t('price_trust_bayern'), t('price_trust_cancel')].map((s, i) => (
          <span key={i} style={{ fontSize: 12.5, color: T.sub, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 4, height: 4, borderRadius: '50%', background: T.mute }} />{s}
          </span>
        ))}
      </div>

      <style>{`.rp-press{transition:transform .12s ease, background .15s, border-color .15s}.rp-press:active{transform:scale(.96)}@media (prefers-reduced-motion: reduce){.rp-press{transition:background .15s, border-color .15s}.rp-press:active{transform:none}}`}</style>
    </div>
  );

  // ── Overlay mode (LimitGateModal / UpgradeCTA) ────────────────────────────
  if (onClose) {
    const overlayMaxW = isDesktop ? 1120 : isTablet ? 760 : 480;
    return (
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 210,
          background: 'rgba(0,0,0,0.62)',
          display: 'flex', justifyContent: 'center', overflowY: 'auto',
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            width: '100%', maxWidth: overlayMaxW,
            background: T.bg, color: T.text, fontFamily: F.family, minHeight: '100vh',
          }}
        >
          <div style={{
            position: 'sticky', top: 0, zIndex: 2, background: T.bg,
            padding: '18px 24px 16px', borderBottom: `1px solid ${T.line}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <h1 style={{ fontFamily: F.display, fontSize: 22, fontWeight: 800, letterSpacing: '-0.035em', margin: 0, color: T.text }}>{t('plans')}</h1>
            <button
              className="mo-close"
              onClick={onClose}
              style={{
                width: 34, height: 34, borderRadius: 16,
                background: 'transparent', border: `1px solid ${T.line}`,
                color: T.sub, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
              }}
            >
              <svg className="mo-close-ico" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', flexShrink: 0 }}>
                <path d="M18 6L6 18" /><path d="M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div style={{ padding: isMobile ? '24px 18px 32px' : '28px 24px 36px' }}>
            {inner}
          </div>
        </div>
      </div>
    );
  }

  return inner;
}

// ── Report-Hero: das Dokument, das man kauft (Held, kein Abo) ─────────────────
function ReportHero({ T, t, report, isMobile, meta, busy, onCta }) {
  const { ctaLabel, ctaDisabled } = meta;
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 300px',
      gap: 28, alignItems: 'center',
      background: T.surfaceHi, border: `1px solid ${T.line}`, borderRadius: 22,
      padding: isMobile ? '26px 22px' : '34px 36px',
      boxShadow: `${T.cardInset}, ${T.cardShadow}`,
    }}>
      <div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 700, color: RED, letterSpacing: '-0.01em', marginBottom: 10 }}>
          <span style={{ width: 6, height: 6, borderRadius: 1.4, background: RED }} />{t('price_report_kicker')}
        </div>
        <h3 style={{ fontFamily: F.display, fontSize: isMobile ? 26 : 30, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.05, margin: 0, color: T.text }}>
          {t('price_report_headline')}
        </h3>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, margin: '16px 0 2px' }}>
          <span style={{ fontFamily: F.num, fontSize: 54, fontWeight: 800, letterSpacing: '-0.05em', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{report.price}</span>
          <span style={{ fontSize: 14, color: T.sub, fontWeight: 500 }}>{report.note}</span>
        </div>
        <div style={{
          display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '11px 26px', margin: '20px 0 24px', maxWidth: 560,
        }}>
          {report.feats.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, fontWeight: 500, lineHeight: 1.35 }}>
              <Check stroke={T.gain} />{f}
            </div>
          ))}
        </div>
        <button
          className="rp-press"
          onClick={ctaDisabled || busy ? undefined : onCta}
          disabled={ctaDisabled || busy}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '13px 22px',
            background: ctaDisabled ? 'transparent' : T.text,
            color: ctaDisabled ? T.sub : T.bg,
            border: ctaDisabled ? `1.5px solid ${T.line}` : 'none',
            borderRadius: 13, cursor: ctaDisabled || busy ? 'default' : 'pointer',
            fontFamily: F.family, fontWeight: 700, fontSize: 14, letterSpacing: '-0.01em',
            opacity: busy ? 0.8 : 1,
          }}
        >
          {busy ? t('price_activating') : ctaLabel}
        </button>
      </div>

      {/* Dokument-Illustration (nur ab Tablet) */}
      {!isMobile && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{
            position: 'relative', width: 200, height: 248,
            background: T.bg, border: `1px solid ${T.line}`, borderRadius: 12,
            boxShadow: T.cardShadow, padding: '22px 20px', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: 0, right: 0, width: 40, height: 40, background: T.lineSoft, borderBottomLeftRadius: 12 }} />
            <div style={{ fontSize: 11, fontWeight: 700, color: T.sub, letterSpacing: '0.02em' }}>{t('price_report_doc_title')} · {YEAR}</div>
            <DocLine T={T} w="80%" /><DocLine T={T} w="55%" />
            <div style={{ marginTop: 16, display: 'flex', alignItems: 'flex-end', gap: 6, height: 60 }}>
              {[40, 62, 48, 78, 58, 90].map((h, i) => (
                <span key={i} style={{ flex: 1, height: `${h}%`, borderRadius: '3px 3px 0 0', background: T.lineSoft }} />
              ))}
            </div>
            <DocLine T={T} w="70%" mt={16} /><DocLine T={T} w="42%" />
            <div style={{ position: 'absolute', bottom: 18, left: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
              <MCGridMark size={22} cells={T.mute} dot={RED} plus="#fff" />
              <span style={{ fontSize: 9.5, fontWeight: 700, color: T.mute, letterSpacing: '0.04em' }}>{t('price_report_seal')}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DocLine({ T, w, mt = 9 }) {
  return <div style={{ height: 6, borderRadius: 3, background: T.lineSoft, width: w, marginTop: mt }} />;
}

// ── Pass-Karte: eine Mitgliedschaft als red.-Karte (MemberCard-Palette) ───────
function PassCard({ pal, plan, meta, busy, activating, onCta }) {
  const { ctaLabel, ctaDisabled } = meta;
  return (
    <div style={{
      position: 'relative', borderRadius: 20, padding: '26px 24px 24px',
      overflow: 'hidden', display: 'flex', flexDirection: 'column',
      minHeight: 430, background: pal.bg, boxShadow: pal.shadow,
    }}>
      {/* geprägtes „+"-Wasserzeichen, überhängend oben rechts (vergrößert auf 200) */}
      <MCGridMark
        size={200} cells={pal.emboss.cells} dot={pal.emboss.dot} plus={pal.emboss.plus}
        style={{ position: 'absolute', right: -40, top: -40, pointerEvents: 'none' }}
      />

      {/* Wortmarke + Tier auf gemeinsamer Baseline (wie die echte Karte) */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, position: 'relative' }}>
        <MCGridMark size={21} cells={pal.cells} dot={RED} plus="#fff" style={{ alignSelf: 'center' }} />
        <Wordmark size={20} color={pal.ink} dot={RED} />
        <span style={{
          fontFamily: F.brand, fontWeight: 600, fontSize: 14, letterSpacing: '0.01em',
          textTransform: 'lowercase', color: pal.faint,
          display: 'inline-flex', alignItems: 'baseline',
        }}>
          {plan.tier.toLowerCase()}
          {plan.tier !== 'Free' && <span style={{ width: 3, height: 3, borderRadius: 1, background: RED, marginLeft: 2 }} />}
        </span>
      </div>

      {/* Preis */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 22, color: pal.ink }}>
        <span style={{ fontFamily: F.num, fontSize: 44, fontWeight: 800, letterSpacing: '-0.05em', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{plan.price}</span>
      </div>
      <div style={{ fontSize: 12.5, color: pal.sub, marginTop: 5, fontWeight: 500 }}>{plan.note}</div>

      {/* Divider */}
      <div style={{ height: 1, background: pal.hair, margin: '20px 0' }} />

      {/* Features */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11, flex: 1 }}>
        {plan.feats.map((f, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, color: pal.ink, fontSize: 13.5, fontWeight: 500, lineHeight: 1.4 }}>
            <Check size={15} stroke={pal.faint} sw={2} />{f}
          </div>
        ))}
      </div>

      {/* CTA */}
      <button
        className="rp-press"
        onClick={ctaDisabled || busy ? undefined : onCta}
        disabled={ctaDisabled || busy}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100%', gap: 8,
          marginTop: 22, padding: '13px 20px', borderRadius: 13,
          fontFamily: F.family, fontWeight: 700, fontSize: 13.5, letterSpacing: '-0.01em',
          cursor: ctaDisabled || busy ? 'default' : 'pointer',
          background: ctaDisabled ? 'transparent' : pal.btnBg,
          color: ctaDisabled ? pal.sub : pal.btnFg,
          border: ctaDisabled ? `1.5px solid ${pal.hair}` : 'none',
          opacity: busy ? 0.8 : 1,
        }}
      >
        {busy ? activating : ctaLabel}
      </button>
    </div>
  );
}
